import { SourcesAction, SourcesActionKeys } from '../actions/sources';
import { UiAction, UiActionKeys } from '../actions/ui';
import { EditorType } from '../constants';
import { INITIAL_PAGE, DEFAULT_WIDTH, DEFAULT_HEIGHT } from './constants';
import { Api } from '../../../typings/api';

export interface PathsState {
	pagePath: string;
	imagePath: string;
	thumbnailPath: string;
	spreadPath?: string;
	cssPath?: string;
	themesCssPath?: string;
	jsPath?: string;
	configsPath?: string;
	accessiblePagePath?: string;
	accessibleUrl?: string;
}

export interface PageData {
	page: number;
	defaultHtml: string;
	html: string;
	width?: number;
	height?: number;
	fontsPath?: string;
	googleFontsPath?: string;
	externalId?: number;
}

export interface PageDataState {
	[k: number]: PageData;
}

export interface ResourcesState {
	css: string;
	themesCss?: string;
	scripts?: string;
}

export interface PublishPriceState {
	actionCredits: Api.CustomActionCredits;
	clientId?: number;
	globalId?: number;
}

const pathsInitialState: PathsState = {
	pagePath: '',
	cssPath: '',
	themesCssPath: '',
	jsPath: '',
	configsPath: '',
	imagePath: '',
	thumbnailPath: '',
	spreadPath: '',
};

const getPageDefaultData = (page: number, width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT): PageData => ({
	page,
	defaultHtml: '',
	html: '',
	width,
	height,
	fontsPath: '',
	googleFontsPath: '',
});

const pagesInitialState: PageDataState = {
	[INITIAL_PAGE]: getPageDefaultData(INITIAL_PAGE),
};

const previewPagesInitialState: number[] = [];

const resourcesInitialState: ResourcesState = {
	css: '',
};

const publishPriceInitialState: Api.PublishPrice = {
	actionCredits: {},
};

export const paths = (state: PathsState = pathsInitialState, action: SourcesAction | UiAction): PathsState => {
	switch (action.type) {
		case SourcesActionKeys.SET_DATA: {
			if (action.editorType === EditorType.INDESIGN) {
				const {
					pagePath,
					cssPath,
					themesCssPath,
					jsPath,
					configsPath,
					imagePath,
					thumbnailPath,
					spreadPath,
					accessiblePagePath,
					accessibleUrl,
				} = action.data as Api.IndesignEditorGeneralData;
				return {
					...state,
					pagePath,
					cssPath,
					themesCssPath,
					jsPath,
					configsPath,
					imagePath,
					thumbnailPath,
					spreadPath,
					accessiblePagePath,
					accessibleUrl,
				};
			} else {
				const { pagePath } = action.data as Api.HtmlEditorData;
				return {
					...state,
					pagePath,
				};
			}
		}
		case UiActionKeys.RESET:
			return pathsInitialState;
		default:
			return state;
	}
};

export const pages = (state: PageDataState = pagesInitialState, action: SourcesAction | UiAction): PageDataState => {
	switch (action.type) {
		case SourcesActionKeys.ADD_PAGE: {
			const { page, width, height } = action;
			return {
				...state,
				[page]: getPageDefaultData(page, width, height),
			};
		}
		case SourcesActionKeys.SET_PAGE_DATA:
			return {
				...state,
				[action.data.page]: {
					...state[action.data.page],
					...action.data,
				},
			};
		case UiActionKeys.RESET:
			return pagesInitialState;
		default:
			return state;
	}
};

export const previewPages = (state: number[] = previewPagesInitialState, action: SourcesAction | UiAction): number[] => {
	switch (action.type) {
		case SourcesActionKeys.SET_DATA: {
			if (action.editorType === EditorType.INDESIGN) {
				const { nonEpubPages } = action.data as Api.IndesignEditorGeneralData;
				return nonEpubPages
					? [...state, ...nonEpubPages]
					: state;
			} else {
				return state;
			}
		}
		case UiActionKeys.RESET:
			return previewPagesInitialState;
		default:
			return state;
	}
};

export const resources = (state: ResourcesState = resourcesInitialState, action: SourcesAction | UiAction): ResourcesState => {
	switch (action.type) {
		case SourcesActionKeys.SET_RESOURCES_DATA:
			return {
				...state,
				...action.data,
			};
		case UiActionKeys.RESET:
			return resourcesInitialState;
		default:
			return state;
	}
};

export const parentFolderId = (state: number | null = null, action: SourcesAction | UiAction): number | null => {
	switch (action.type) {
		case SourcesActionKeys.SET_PARENT_FOLDER_ID:
			return action.id;
		case UiActionKeys.RESET:
			return null;
		default:
			return state;
	}
};

export const invoiceStatus = (state: Api.AssetItemInvoiceStatus | null = null, action: SourcesAction | UiAction): Api.AssetItemInvoiceStatus | null => {
	switch (action.type) {
		case SourcesActionKeys.SET_DATA: {
			if (action.editorType === EditorType.INDESIGN) {
				const { invoiceStatus } = action.data as Api.IndesignEditorGeneralData;
				return invoiceStatus ? invoiceStatus : state;
			} else {
				return state;
			}
		}
		default:
			return state;
	}
};

export const publishPrice = (state: PublishPriceState = publishPriceInitialState, action: SourcesAction): PublishPriceState => {
	switch (action.type) {
		case SourcesActionKeys.SET_PUBLISH_PRICE:
			return {
				...state,
				...action.data,
			};
		default:
			return state;
	}
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/reducer/sources.ts